import logo from "./logo.png";
import Loader from "../Loader/Loader";
import { Feedback } from "../Feedback/Feedback";
import {
  ReactNode,
  RefObject,
  createElement,
  useCallback,
  useContext,
  useEffect,
} from "react";
import Markdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";

import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyButton from "../Shared/CopyButton/CopyButton";
import { renderToString } from "react-dom/server";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ReplayIcon from "@mui/icons-material/Replay";
import { useAuth } from "../../auth/AuthContext";
import { ChatContext } from "./ChatContainer";
import CropFreeIcon from "@mui/icons-material/CropFree";
import ShortTextIcon from "@mui/icons-material/ShortText";
import CodeIcon from "@mui/icons-material/Code";
import React from "react";
import useGetPdfById from "./useGetPdfById";

export interface ChatMessage {
  isAi: boolean;
  content: string;
  typing?: boolean;
  uniqueId?: string;
  imgURLs?: string[];
  comment?: string;
  streaming?: string;
  citations?: {
    pdfID: string;
    pageNumber: number;
  }[];
  error: boolean;
}

interface ImportCodeButtonProps {
  children: ReactNode | ReactNode[];
  importCode: (code: string) => void;
}

const ImportCodeButton: React.FC<ImportCodeButtonProps> = ({
  children,
  importCode,
}) => {
  const handleImport = useCallback(
    (reactChildren: ReactNode | ReactNode[]) => {
      const textString = renderToString(
        createElement("div", null, reactChildren)
      );
      const tempElement = document.createElement("div");
      tempElement.innerHTML = textString;
      const unencodedText = tempElement.textContent || "";

      importCode(unencodedText);
    },
    [importCode]
  );

  return (
    <Button
      variant="contained"
      startIcon={<ExitToAppIcon />}
      onClick={() => handleImport(children)}
    >
      Import Code to IDE
    </Button>
  );
};

const RAGIcon = () => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      <CropFreeIcon />
      <ShortTextIcon
        style={{
          fontSize: "0.9em",
          position: "absolute",
        }}
      />
    </Stack>
  );
};

const CodeInterpreterIcon = () => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      <CropFreeIcon />
      <CodeIcon
        style={{
          fontSize: "0.9em",
          position: "absolute",
        }}
      />
    </Stack>
  );
};

const renderText = (text: string, importCode: (code: string) => void) => {
  // remove tool call from text and create a flag
  const ragCall = /# Using RAG Tool:\n\n/s;
  let ragCalled = false;
  if (ragCall.test(text)) {
    text = text.replace(ragCall, "");
    ragCalled = true;
  }
  const codeInterpreterCall = /# Using Code Interpreter Tool:\n\n/s;
  let codeInterpreterCalled = false;
  if (codeInterpreterCall.test(text)) {
    text = text.replace(codeInterpreterCall, "");
    codeInterpreterCalled = true;
  }

  // console.log("text", text);

  return (
    <Typography component="div">
      {(ragCalled || codeInterpreterCalled) && (
        <Box display="inline-block" padding={1}>
          {ragCalled && (
            <Tooltip title="RAG Tool">
              <Typography display="inline-block">
                <RAGIcon />
              </Typography>
            </Tooltip>
          )}{" "}
          {codeInterpreterCalled && (
            <Tooltip title="Code Interpreter Tool">
              <Typography display="inline-block">
                <CodeInterpreterIcon />
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}
      <Markdown
        children={text}
        components={{
          code(props) {
            const { children, className, node, ref, ...rest } = props;
            const match = /language-(\w+)/.exec(className || "");
            return match ? (
              <Card>
                <CardHeader
                  title={match[1] || ""}
                  action={
                    <ButtonGroup
                      variant="contained"
                      aria-label="Basic button group"
                    >
                      <ImportCodeButton
                        importCode={importCode}
                        children={children}
                      />
                      <CopyButton>{children}</CopyButton>
                    </ButtonGroup>
                  }
                />
                <SyntaxHighlighter
                  {...rest}
                  PreTag="div"
                  children={String(children).replace(/\n$/, "")}
                  language={match[1]}
                  style={tomorrow}
                />
              </Card>
            ) : (
              <code {...rest} className={className}>
                {children}
              </code>
            );
          },
        }}
      />
    </Typography>
  );
};

interface RetryProps {
  handleSendRetryMessage: (message: ChatMessage) => void;
  message: ChatMessage;
}

const Retry: React.FC<RetryProps> = ({ handleSendRetryMessage, message }) => {
  return (
    <Box className="retry">
      <Grid container alignItems="flex-start" margin="1em 0">
        <Grid item xs>
          <Typography>Something went wrong. Please try again.</Typography>
        </Grid>
        <Grid item xs>
          <Button
            variant="contained"
            className="retry-button button"
            onClick={() => handleSendRetryMessage(message)}
            startIcon={<ReplayIcon />}
          >
            Retry
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Render chat messages
const renderChatMessages = (
  chatMessages: ChatMessage[],
  handleSendRetryMessage: (message: ChatMessage) => void,
  messageEndRef: React.RefObject<HTMLDivElement>,
  importCode: (code: string) => void,
  getPdfById: (pdfId: string) => Promise<any>
) => {
  return chatMessages.map((message, index) => {
    const { user } = useAuth();

    const context = useContext(ChatContext);

    const jumpToPage = context.pageNavigationPluginInstance?.jumpToPage;

    const setCurrentPDFID = context.setCurrentPDFID;
    const currentPDFID = context.currentPDFID;

    const setPdfContent = context.setPdfContent;

    // Check if the message content includes a YouTube link
    if (message.error) {
      return (
        <Box key={index}>
          <Paper elevation={3}>
            <Grid container alignItems="flex-start">
              <Grid item xs={1} margin="1em 0" padding={1}>
                <Avatar src={logo}>
                  <SmartToyIcon />
                </Avatar>
              </Grid>
              <Grid item xs={11} id={message.uniqueId || undefined}>
                <Retry
                  handleSendRetryMessage={handleSendRetryMessage}
                  message={message}
                />
              </Grid>
            </Grid>
            <Box ref={messageEndRef} />
          </Paper>
        </Box>
      );
    }

    return (
      <Box key={index}>
        <Paper elevation={3}>
          <Grid container alignItems="flex-start" padding={1}>
            <Grid item xs={1} margin="1em 0">
              <Avatar
                alt={message.isAi ? "bot" : "user"}
                src={message.isAi ? logo : ""}
              >
                {message.isAi ? (
                  <SmartToyIcon />
                ) : (
                  user?.username.charAt(0).toUpperCase()
                )}
              </Avatar>
              {/* <img
              src={message.isAi ? botSvg : userSvg}
              alt={message.isAi ? "bot" : "user"}
            /> */}
            </Grid>
            <Grid item xs={11} id={message.uniqueId || undefined}>
              {message.typing && <Loader />}
              {!message.typing && renderText(message.content, importCode)}
              {message.citations && message.citations.length > 0 && (
                <Box>
                  {message.citations
                    .sort((a, b) => a.pageNumber - b.pageNumber)
                    .map((citation, index) => (
                      <Button
                        key={index}
                        variant="text"
                        onClick={async () => {
                          if (
                            currentPDFID &&
                            setCurrentPDFID &&
                            setPdfContent &&
                            citation.pdfID !== currentPDFID
                          ) {
                            setCurrentPDFID(citation.pdfID);
                            setPdfContent("");
                            const pdf = await getPdfById(citation.pdfID);
                            setPdfContent(pdf.documentContent);
                            setTimeout(() => {
                              if (jumpToPage)
                                jumpToPage(citation.pageNumber - 1);
                            }, 300);
                          } else {
                            if (jumpToPage) jumpToPage(citation.pageNumber - 1);
                          }
                          console.log(citation.pdfID, citation.pageNumber);
                        }}
                      >
                        {"["}Page {citation.pageNumber}
                        {"]"}
                      </Button>
                    ))}
                </Box>
              )}
              {!message.typing && message.isAi && message.uniqueId && (
                <Feedback index={message.uniqueId} />
              )}
            </Grid>
          </Grid>
          <Box ref={messageEndRef} />
        </Paper>
      </Box>
    );
  });
};

interface ChatMessagesProps {
  chatMessages: ChatMessage[];
  handleSendRetryMessage: (message: ChatMessage) => void;
  messageEndRef: RefObject<HTMLDivElement>;
  importCode: (code: string) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  chatMessages,
  handleSendRetryMessage,
  messageEndRef,
  importCode,
}) => {
  // Scroll to the last message when messages update
  const getPdfById = useGetPdfById();

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, messageEndRef]);

  const initialMessages = [
    {
      isAi: true,
      content: "Welcome to Kiwi! How can I help you?",
      error: false,
    },
    // {
    //   isAi: true,
    //   content:
    //     '# Using Code Interpreter Tool:\n\n# Using RAG Tool:\n\nAttention in the context of neural networks, particularly in natural language processing (NLP), is a mechanism that allows models to focus on different parts of the input sequence when generating each part of the output sequence, somewhat akin to how humans pay attention to different aspects of what they\'re perceiving or thinking about at any given moment. This ability to pay selective attention helps in understanding and generating language by dynamically aligning input words (or features) with output words, thus capturing dependencies and relationships in the data, especially in long sentences or sequences where distant elements could be related.\n\nThe essence of the attention mechanism can be distilled into a few key components, often described in the Scaled Dot-Product Attention framework, as seen in one of the images. This framework involves several steps:\n\n1. **Query, Key, and Value (Q, K, V):** Each element of the input sequence is transformed into three representations: Queries, Keys, and Values, typically through separate linear transformations. These are essential for calculating attention weights and the resultant context or output.\n   \n2. **Matrix Multiplication:** The Queries are multiplied with the Keys to produce a matrix indicating the relevance or attention scores between different elements of the sequence.\n\n3. **Scale:** The scores from the previous step are scaled down, usually by the square root of the dimension of the key vectors, to help stabilize gradients during training.\n\n4. **Mask (Optional):** A mask can be applied to the scores before softmax, primarily to ignore or mask out certain positions within the input or output sequences, usually for handling variable sequence lengths or ensuring causality in language models.\n\n5. **SoftMax:** The SoftMax function is applied to the scores to generate a probability distribution for each word, indicating the weight of attention that should be paid to each word in the sequence when generating the output.\n\n6. **Output Generation:** Finally, the softmax-weighted values are summed up to produce the output of the attention step, which then serves as input for further processing in the model.\n\nThrough these steps, the attention mechanism allows models to dynamically weigh the importance of different parts of the input data, effectively enabling the model to "focus" on relevant information when performing tasks such as translation, summarization, and text generation. This is crucial for dealing with long-range dependencies and understanding the context in sequences, manifesting in improved model performance across a variety of NLP tasks.',
    //   error: false,
    //   citations: [
    //     {
    //       pdfID: "13eac401-5691-42a8-a351-90747fc26ad3",
    //       pageNumber: 13,
    //     },
    //     {
    //       pdfID: "13eac401-5691-42a8-a351-90747fc26ad3",
    //       pageNumber: 4,
    //     },
    //   ],
    // },
    //     {
    //       isAi: true,
    //       content: "Hello! I'm a chatbot. How can I help you?",
    //       error: true,
    //     },
    //     {
    //       isAi: false,
    //       content: "Hello!",
    //       error: false,
    //     },
    //     {
    //       isAi: true,
    //       content: `As an AI language model, I am not equipped to interpret images directly. However, given the description of an 'attention mechanism' visualization, the python code to implement such an attention mechanism could be something like the following. Note that this is a high-level implementation snippet which requires full knowledge of transformers theory and many python libraries like PyTorch, as well as the context where it will be used:

    // \`\`\`python
    //       import torch.nn as nn
    //       import torch

    //       class MultiHeadAttention(nn.Module):
    //       def __init__(self, heads, d_model, dropout = 0.1):
    //         super().__init__()

    //         self.d_model = d_model
    //         self.d_k = d_model // heads
    //         self.h = heads
    //         self.attn = None

    //         self.q_linear = nn.Linear(d_model, d_model)
    //         self.v_linear = nn.Linear(d_model, d_model)
    //         self.k_linear = nn.Linear(d_model, d_model)
    //         self.dropout = nn.Dropout(dropout)
    //         self.out = nn.Linear(d_model, d_model)

    //       def forward(self, q, k, v, mask=None):
    //         bs = q.size(0)

    //         # perform linear operation and split into N heads
    //         k = self.k_linear(k).view(bs, -1, self.h, self.d_k)
    //         q = self.q_linear(q).view(bs, -1, self.h, self.d_k)
    //         v = self.v_linear(v).view(bs, -1, self.h, self.d_k)

    //         # transpose to get dimensions bs * N * sl * d_model
    //         k = k.transpose(1,2)
    //         q = q.transpose(1,2)
    //         v = v.transpose(1,2)

    //         # calculate attention
    //         scores = attention(q, k, v, self.d_k, mask, self.dropout)

    //         # concatenate heads
    //         concat = scores.transpose(1,2).contiguous().view(bs, -1, self.d_model)

    //         # final linear layer
    //         output = self.out(concat)

    //         return output
    // \`\`\`

    // This python code is a fundamental part of the Transformer architecture called MultiHeadAttention. It takes the query, key, and value and calculates the attention weights (scores) for each head. Each head learns to give attention to different parts of the sentence, representing different aspects of information.

    // Please note that the function "attention()" is not provided, it's a placeholder for the actual implementation of the attention mechanism, which could be scaled dot-product attention or another type of attention. You can implement it depending on the specifics of your project. Also, the correct functioning of the transformer requires many other module like PositionwiseFeedforward, LayerNormalization, etc.

    // This is a high-level python code snippet, please make sure to thoroughly understand Multihead Attention and Transformers theory before using it.`,
    //       error: false,
    //     },
  ];

  chatMessages = initialMessages.concat(chatMessages);

  return (
    <Stack padding={1} spacing={1}>
      {renderChatMessages(
        chatMessages,
        handleSendRetryMessage,
        messageEndRef,
        importCode,
        getPdfById
      )}
    </Stack>
  );
};

export default ChatMessages;
